import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import {
  fetchApproveWormRequest,
  fetchWormRequestList,
  WormRequestItem,
} from "../../../api/admin";
import { useAuthStore } from "../../../atoms/user.auth.store";
import ContentsTopHeader from "../../../components/common/contents.top.header";
import { UserListContainer } from "../../../style/contents/backup/backup";
import { NewTableContainer } from "../../../style/contents/backup/table";

type WormRequestRow = WormRequestItem & {
  fileName?: string | null;
  filePath?: string | null;
  path?: string | null;
  name?: string | null;
};

const getPathName = (path?: string | null) => {
  if (!path) return "-";

  const normalizedPath = path.replace(/[\\/]+$/, "");
  const name = normalizedPath.split(/[\\/]/).pop();

  return name || normalizedPath || "-";
};

const getRequestTypeLabel = (type: string) => {
  if (type === "file") return "파일";
  if (type === "directory") return "폴더";

  return type || "-";
};

const getRequestStatusLabel = (status: string) => {
  if (status === "requested") return "요청";
  if (status === "confirmed") return "완료";

  return status || "-";
};

const getFileName = (item: WormRequestRow) => {
  if (item.requestType !== "file") return "-";

  return (
    item.fileName ||
    item.name ||
    getPathName(item.filePath || item.path) ||
    (item.fileId ? `fileId: ${item.fileId}` : "-")
  );
};

const toSearchText = (item: WormRequestRow) =>
  [
    item.deviceId,
    getPathName(item.directoryPath),
    getFileName(item),
    getRequestTypeLabel(item.requestType),
    getRequestStatusLabel(item.requestStatus),
  ]
    .map((value) => String(value ?? "").toLowerCase())
    .join(" ");

const WormList = () => {
  const { role } = useAuthStore();
  const [data, setData] = useState<WormRequestRow[]>([]);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sorting, setSorting] = useState<SortingState>([
    { id: "requestedAt", desc: true },
  ]);

  const adminUserId = localStorage.getItem("userId") ?? "";

  const loadWormRequestList = useCallback(async () => {
    if (!adminUserId) return;

    setIsLoading(true);
    try {
      const response = await fetchWormRequestList(adminUserId);
      setData(response.wormRequestList || []);
    } catch (err) {
      console.error("WORM 요청 리스트 조회 실패:", err);
      alert("WORM 요청 리스트 조회 중 오류가 발생했습니다.");
    } finally {
      setIsLoading(false);
    }
  }, [adminUserId]);

  const handleApprove = useCallback(
    async (wormRequestId: number) => {
      if (!adminUserId) {
        alert("관리자 정보가 없습니다.");
        return;
      }

      try {
        await fetchApproveWormRequest(adminUserId, { wormRequestId });
        alert("WORM 요청이 승인되었습니다.");
        loadWormRequestList();
      } catch (err) {
        console.error("WORM 요청 승인 실패:", err);
        alert("WORM 요청 승인 중 오류가 발생했습니다.");
      }
    },
    [adminUserId, loadWormRequestList],
  );

  const filteredData = useMemo(() => {
    const keyword = searchKeyword.trim().toLowerCase();
    if (!keyword) return data;

    return data.filter((item) => toSearchText(item).includes(keyword));
  }, [data, searchKeyword]);

  const columns = useMemo<ColumnDef<WormRequestRow>[]>(
    () => [
      {
        header: "디바이스ID",
        accessorKey: "deviceId",
        cell: (info) => info.getValue() || "-",
        size: 220,
        enableSorting: true,
      },
      {
        header: "폴더명",
        accessorKey: "directoryPath",
        cell: ({ row }) =>
          row.original.requestType === "directory"
            ? getPathName(row.original.directoryPath)
            : "-",
        size: 260,
        enableSorting: true,
      },
      {
        header: "파일명",
        id: "fileName",
        cell: ({ row }) => getFileName(row.original),
        size: 260,
        enableSorting: false,
      },
      {
        header: "타입",
        accessorKey: "requestType",
        cell: (info) => getRequestTypeLabel(String(info.getValue() || "")),
        size: 140,
        enableSorting: true,
      },
      {
        header: "상태",
        accessorKey: "requestStatus",
        cell: (info) => getRequestStatusLabel(String(info.getValue() || "")),
        size: 140,
        enableSorting: true,
      },
      {
        header: "승인버튼",
        id: "approve",
        cell: ({ row }) => (
          <button
            type="button"
            className="worm-approve-btn"
            disabled={row.original.requestStatus !== "requested"}
            onClick={() => handleApprove(row.original.wormRequestId)}
          >
            승인
          </button>
        ),
        size: 140,
        enableSorting: false,
      },
    ],
    [handleApprove],
  );

  const table = useReactTable({
    data: filteredData,
    columns,
    state: {
      sorting,
    },
    enableSorting: true,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  useEffect(() => {
    loadWormRequestList();
  }, [loadWormRequestList]);

  if (role === "user") {
    return <Navigate to="/404" replace />;
  }

  return (
    <UserListContainer className="worm-list-page">
      <ContentsTopHeader
        title="WORM 리스트"
        onSearch={setSearchKeyword}
        pageSizeHidden
      />
      <div className="contents-body">
        <NewTableContainer className="new_design_">
          <div className="table-head-wrap">
            <div className="left">
              <span className="table-total-row">총 {table.getRowCount()}</span>
            </div>
          </div>
          {isLoading ? (
            <div className="loading">데이터를 불러오는 중입니다...</div>
          ) : (
            <div className="fixed-height">
              <table>
                <thead>
                  {table.getHeaderGroups().map((item) => (
                    <tr key={item.id}>
                      {item.headers.map((header) => (
                        <th
                          key={header.id}
                          style={{
                            width: `${header.getSize()}px`,
                            cursor: header.column.getCanSort()
                              ? "pointer"
                              : undefined,
                          }}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                          {{
                            asc: (
                              <span className="up">
                                <img src="/assets/left-arrow.png" alt="" />
                              </span>
                            ),
                            desc: (
                              <span className="down">
                                <img src="/assets/left-arrow.png" alt="" />
                              </span>
                            ),
                          }[header.column.getIsSorted() as string] ?? null}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.length === 0 ? (
                    <tr>
                      <td colSpan={columns.length} className="no-data-row">
                        검색 결과가 없습니다.
                      </td>
                    </tr>
                  ) : (
                    table.getRowModel().rows.map((row) => (
                      <tr key={row.id}>
                        {row.getVisibleCells().map((cell) => (
                          <td key={cell.id}>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </td>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

        </NewTableContainer>
      </div>
    </UserListContainer>
  );
};

export default WormList;
